import React, { useState, useEffect } from "react";
import dynamic from 'next/dynamic';
import DtvText, { IDtvTextProps } from "../../video-lab/dtv-text";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import { IDtvButtonProps } from "@directv-digital/dds.ui.dtv-button";
import { colorPalette, MOBILE_WIDTH } from "@directv-digital/dds.ui.dtv-theme";
import { KeyValuePair } from "@directv-digital/dds.services.channel-lineup-provider";
import { IconCategory, AddOnIconCategory } from "./components";
import { IMyFreeConfig, IPackage } from "../chnlListingPage.props";
import { ZipcodeModuleProps } from "@/video-lab/zipcode-module";
import { IChnlLineupThemeUI } from "../server/themeConfig";

const IconBannerErrorPage = dynamic(() => import('./components/BannerErrorPage').then(mod => mod.IconBannerErrorPage));

/**
 * [WAT-11772] A genre pack's subscription row, plus the offer code the adapter stamps on it.
 * Declared rather than read through `(svod as any)?.offerCode`: the code is the PRIMARY match
 * key now, so a rename or drop of the adapter's stamp must be a compile error, not a silent
 * revert to label-only matching. Mirrors `ChannelRow` in ChlupStore/filterMethods.ts, which
 * exists for exactly this reason on the channel side.
 */
export type PackSvod = IPackage["svods"][number] & { offerCode?: string };

export interface IconBannerProps {
  id: string;
  bannerChannels?: any;
  bannerCategories?: KeyValuePair[];
  bannerPackages?: IPackage[];
  isSearching: boolean;
  // [WAT-11765] SVOD names currently in the channel search, so a subscription-only
  // search can still resolve the genre packs that include them.
  searchedSvodNames?: string[];
  // [WAT-11772] Offer codes of the searched subscriptions. The PRIMARY match key: the chip's
  // label and the pack row's label are resolved from different sources and demonstrably
  // disagree for the same product, so comparing labels left a genre-pack subscription on
  // "not available" whatever the customer picked. `searchedSvodNames` stays as the fallback
  // for an entry authored with no offer code.
  // [WAT-11772] The searched subscriptions as {code, name} PAIRS.
  //
  // 🔴 Deliberately pairs, not two parallel arrays. With a separate code list and name list
  // the matcher intersected them independently, so a row matched when its code came from ONE
  // selection and its label from ANOTHER: selecting the DUO bundle's "Hulu" half plus a
  // standalone "Disney+" on a different code lit the DUO "Disney+" row too, because DUO was in
  // the code set and "Disney+" was in the name set. A pair can only match as a whole.
  searchedSvods?: { offerCode?: string; name?: string }[];
  // [WAT-11765] True whenever ANY search is active, subscription or channel. The
  // Subscriptions row is narrowed on this, NOT on searchedSvodNames — a channel-only
  // search has no svod names but must still stop the pack showing its full
  // subscription list (WAT-11717 item 7: "selecting only a particular channel —
  // svods/subscriptions are also coming up").
  searchActive?: boolean;
  showMoreData?: any[];
  updateShowMoreCategories: (data: any) => void;
  openChannelModal: (channel: any) => void;
  zipCodeModule?: ZipcodeModuleProps;
  categoryFooterCtas?: {
    [key: string]: IDtvButtonProps[]; // key is the category id
  };
  localChnlBadgeTooltip?: IDtvTextProps["tooltip"];
  rsnChnlBadgeTooltip?: IDtvTextProps["tooltip"];
  // WAT-11225: CMS heading for the genre pack included-SVOD row
  subscriptionsLabel?: string;
  channelsLabel?: string;
  internetChnlsPDFLink?: IDtvButtonProps;
  theming?: IChnlLineupThemeUI["categoriesTab"]["category"]
  noMatchPageMsg?: string;
  genreNationals?: any;
  myFreeConfig?: IMyFreeConfig;
}

const normalizeString = (str: string) => str?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[\s_&]+/g, "");

const IconBannerWAddOnChnls = (props: IconBannerProps) => {
  try {
    const {
      bannerChannels,
      bannerCategories,
      categoryFooterCtas,
      zipCodeModule: zipCodeModuleProps,
      localChnlBadgeTooltip,
      rsnChnlBadgeTooltip,
      subscriptionsLabel,
      channelsLabel,
      theming,
      noMatchPageMsg,
      myFreeConfig,
      searchedSvodNames,
      searchedSvods,
      searchActive
    } = props;

    const [showMoreKeys, setShowMoreKeys] = useState<any>();
    const [trackedCategories, setTrackedCategories] = useState<any[]>([]);

    useEffect(() => {
      handleResize();
      ['load', 'resize'].forEach(evt => window?.addEventListener(evt, handleResize))
      return () => ['load', 'resize'].forEach(evt => window?.removeEventListener(evt, handleResize))
    }, []);

    const handleResize = () => {
      const iconCtnrs = document?.getElementsByClassName('chnlCtg-icons') || null;
      if (typeof document != "undefined" && iconCtnrs) {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < iconCtnrs?.length; i++) {
          const ctnr = iconCtnrs[i] as HTMLElement || null;
          if (ctnr) {
            calcShowMore(ctnr)
          }
        }
      }
    };

    useEffect(() => {
      const iconCtnrs = document?.getElementsByClassName('chnlCtg-icons') || null;
      if (iconCtnrs && bannerCategories?.length !== trackedCategories?.length) {
        if (iconCtnrs) {
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < iconCtnrs?.length; i++) {
            const ctnr = iconCtnrs[i] as HTMLElement || null;
            if (ctnr) {
              calcShowMore(ctnr)
            }
          }
        }
        setTrackedCategories(bannerCategories);
      }
    }, [bannerCategories]);

    const calcShowMore = iconCtnr => {
      // eslint-disable-next-line camelcase
      const ctg_key = iconCtnr?.getAttribute("id");
      const smElem: HTMLElement = iconCtnr.nextSibling;
      const { scrollHeight, clientHeight } = iconCtnr;
      if (iconCtnr && smElem && window?.innerWidth < MOBILE_WIDTH) {
        // eslint-disable-next-line camelcase
        if ((clientHeight < scrollHeight - 5 || scrollHeight > 478) || showMoreKeys?.[ctg_key]) {
          smElem.style.display = 'block';
          smElem.style.visibility = 'visible';
        } else {
          smElem.style.display = 'none';
          smElem.style.visibility = 'hidden';
        }
      } else {
        smElem.style.display = 'none';
        smElem.style.visibility = 'hidden';
      }
    };

    // eslint-disable-next-line camelcase
    const onShowMore = (ctg_key: string) => {
      console.log("onShowMore", ctg_key, "showMoreData", props?.showMoreData);
      // eslint-disable-next-line camelcase
      const nextSMVal = !(props?.showMoreData?.[ctg_key] || false);
      const nextSM = {
        ...props?.showMoreData,
        // eslint-disable-next-line camelcase
        [ctg_key]: nextSMVal
      }
      // eslint-disable-next-line camelcase
      const iconCtnr: HTMLElement = document.querySelector(`[id=${ctg_key}]`);
      iconCtnr.style.maxHeight = nextSMVal === true ? "5000px" : "475px";
      setShowMoreKeys(nextSM);
      props?.updateShowMoreCategories(nextSM);
    }

    // console.debug("Rendering Icon Banner... data: ", {
    //   bannerCategories,
    //   bannerChannels,
    //   bannerPackages: props?.bannerPackages
    // })

    // [WAT-11765] Which genre packs a pure-subscription search keeps alive, and which of
    // their svods matched. Derived from bannerCategories — the same list the map below
    // walks — so a match can never suppress the no-match copy without also rendering a card.
    // [WAT-11772] Return type annotated once, so callers stop casting at every read
    // (`(resolvePackageData(key) as IPackage)?.svods` — a cast immediately re-handled by `?.`).
    const resolvePackageData = (key: string): IPackage | undefined => (
      key === myFreeConfig?.myFreeCategory?.key ?
        myFreeConfig?.packageData :
        props?.bannerPackages?.find(pkg => normalizeString(pkg?.name) === normalizeString(key))
    );
    // [WAT-11772] Code first, label only as a fallback. A pack subscription matches when its
    // offer code is among the searched codes; an entry with no code (or a chip built before
    // the adapter carried one) still falls back to the normalized label, which is what
    // WAT-11765 shipped.
    // Normalised once: each searched subscription as the pair it actually is.
    const searchedSvodPairs = (searchedSvods || [])
      .map(svod => ({
        code: String(svod?.offerCode ?? "").trim(),
        label: normalizeString(svod?.name)
      }))
      .filter(pair => pair.code || pair.label);
    // [WAT-11772] 🔴 An offer code is an OFFER identity, not a ROW identity. A split bundle
    // renders SEVERAL rows under ONE code — Disney+ and Hulu both on `BOLT-DUOBASIC-202411`.
    // Matching on the code alone therefore UNIONED them: selecting the "Hulu" chip lit the
    // Disney+ logo too, and the Packages grid (still name-matched) disagreed with this tab
    // for the same selection. So the code decides alone only when it identifies ONE row on
    // this card; when a card renders several rows under one code, the label qualifies it.
    const sharedCodesOnCard = (svods: PackSvod[] | undefined) => {
      const labelsByCode: { [code: string]: Set<string> } = {};
      (svods || []).forEach((svod: PackSvod) => {
        const code = String(svod?.offerCode ?? "").trim();
        const label = normalizeString(svod?.name);
        if (!code || !label) {
          return;
        }
        labelsByCode[code] = labelsByCode[code] || new Set<string>();
        labelsByCode[code].add(label);
      });
      return new Set(Object.keys(labelsByCode).filter(code => labelsByCode[code].size > 1));
    };
    const svodMatchesSearch = (svod: PackSvod, sharedCodes: Set<string>) => {
      const code = String(svod?.offerCode ?? "").trim();
      const label = normalizeString(svod?.name);
      if (code) {
        // Match a WHOLE pair. A code shared by several rows on this card cannot identify one
        // of them on its own, so there the pair's label has to agree as well.
        return searchedSvodPairs.some(pair => pair.code === code &&
          (sharedCodes.has(code) ? pair.label === label : true));
      }
      // Only fall back to the label when this row has NO code to compare. A row that HAS a
      // code and did not match must stay unmatched, or a chip for one product would rescue
      // an unrelated row whose label happened to collide. Codeless rows are matched by any
      // searched label, including a coded chip's — a codeless row has nothing better.
      return searchedSvodPairs.some(pair => pair.label === label && !!label) ||
        (searchedSvodNames?.some(n => normalizeString(n) === label) ?? false);
    };
    const svodMatchesByCtg: { [key: string]: IPackage["svods"] } = {};
    if (searchedSvodNames?.length > 0 || searchedSvodPairs.length > 0) {
      bannerCategories?.forEach((ctg: any) => {
        const key = ctg?.key;
        if (!key || key === 'ADD_ON_CHANNELS') {
          return;
        }
        const packSvods = resolvePackageData(key)?.svods as PackSvod[] | undefined;
        const sharedCodes = sharedCodesOnCard(packSvods);
        const matched = packSvods?.filter(svod => svodMatchesSearch(svod, sharedCodes)) || [];
        if (matched?.length > 0) {
          svodMatchesByCtg[key] = matched;
        }
      });
    }

    return (
      <DtvBox id={`${props?.id}-channelsbanner`}
        sx={{
          padding: 0,
          margin: 0,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center"
      }}>
        <DtvBox
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            "& div:first-of-type": { marginTop: "0px" }
        }}>
          {Object?.keys(bannerChannels)?.length === 0 && Object?.keys(svodMatchesByCtg)?.length === 0 ? (
            <DtvText
              content={noMatchPageMsg}
              variant="body1"
              sx={{
                fontSize: {
 xs: "16px",
md: "20px"
},
                color: theming?.textColor,
                textAlign: "center",
                margin: "0px 16px 64px"
              }}
            />
          ) : bannerCategories.map((ctg: any, index: any) => {
            const ctgKey = ctg?.key;
            const ctgValue = ctgKey === "MYFREE_DTV" ? ctg?.genreValue : ctg?.value;

            if (ctgKey && ctg?.key !== 'ADD_ON_CHANNELS') {
              if (bannerChannels[ctgKey]?.length > 0 || svodMatchesByCtg[ctgKey]){
                const isLast = !(index < bannerCategories?.filter(ctg => ctg?.key !== 'ADD_ON_CHANNELS')?.length - 1);
                const basePackageData = resolvePackageData(ctgKey);
                // [WAT-11765] While ANY search is active the Subscriptions row shows only the
                // subscriptions that matched — IconCategory reads the pack's STATIC list, so the
                // narrowing has to happen here. Gated on searchActive, NOT on searchedSvodNames:
                // a CHANNEL-only search has no svod names, and gating on those left the pack
                // showing its full subscription list, which is WAT-11717 item 7 ("selecting only
                // a particular channel — svods/subscriptions are also coming up") unfixed on this
                // tab. Empty here is correct then, and mirrors filteredEmbeddedSvods on the grid.
                // [WAT-11772] No `as IPackage` over a possibly-undefined spread. For a
                // category with no matching pack (reachable — `myFreeConfig.packageData` is
                // optional) the old cast blessed the literal `{ svods: [] }` as a full
                // IPackage, so any non-optional read downstream compiled and broke at
                // runtime. Guarding on `basePackageData` keeps the `undefined` that
                // IconCategory's contract is already written for.
                const packageData = searchActive && basePackageData ?
                  {
                    ...basePackageData,
                    svods: svodMatchesByCtg[ctgKey] || []
                  } :
                  basePackageData;
                const footerCta = categoryFooterCtas?.[`${packageData?.name}`] || {};

                return (
                  <IconCategory
                    channels={bannerChannels[ctgKey] || []}
                    ctgIndex={index}
                    ctgKey={ctgKey}
                    ctgValue={ctgValue}
                    footerCta={footerCta}
                    genreNationals={props?.genreNationals}
                    handleShowMoreClick={onShowMore}
                    isLast={isLast}
                    key={`banner-ctg-${ctgValue}`}
                    localChnlBadgeTooltip={localChnlBadgeTooltip}
                    packageData={packageData}
                    rsnChnlBadgeTooltip={rsnChnlBadgeTooltip}
                    showMoreKey={showMoreKeys?.[ctgKey]}
                    subscriptionsLabel={subscriptionsLabel}
                    channelsLabel={channelsLabel}
                    theming={theming}
                    zipCodeModuleProps={zipCodeModuleProps}
                  />
                );
              }
            } else if (ctg?.key === 'ADD_ON_CHANNELS') {
              // [WAT-11772] Guarded. WAT-11765 made the no-match guard above satisfiable by
              // matched SUBSCRIPTIONS alone, so this map now runs with `bannerChannels` fully
              // empty — a subscription-only search matches no linear channel. `Object.keys` of
              // the resulting `undefined` throws, and the thrown error swaps the entire banner
              // for IconBannerErrorPage. Latent rather than live only because this tab's
              // category list does not carry ADD_ON_CHANNELS today; nothing enforces that.
              const addOnChannels = bannerChannels?.ADD_ON_CHANNELS;
              if(Object.keys(addOnChannels || {}).some(key => addOnChannels?.[key]?.length > 0)) {
                return (
                  <DtvBox
                    id="ChnlListingPage-add-on-channels"
                    key={'ADD_ON_CHANNELS'}
                    sx={{
                      display: "flex",
justifyContent: "center",
flexWrap: "wrap",
                      backgroundColor: "#21528C !important",
                      padding: {
                        xs: "24px 14px",
                        sm: "54px 90px",
                        md: "91px 196px"
                      },
                      minWidth: {
                        xs: "calc(100vw - 28px)",
                        sm: "calc(100vw - 180px)",
                        md: "calc(100vw - 392px)",
                        lg: "100%"
                      },
                      order: 99
                    }}>
                      <DtvBox sx={{
 display: "flex",
flexDirection: "column",
width: "100%"
}}>
                        <DtvText
                          content={"ADD-ON CHANNELS"}
                          variant="subhead1medium"
                          sx={{
                            fontSize: {
 xs: "16px",
md: "24px"
},
                            color: colorPalette.neutral_white.main,
                            lineHeight: {
 xs: "20.8px",
sm: "18.2px",
md: "28.8px"
},
                            letterSpacing: {
 xs: "initial",
md: "-0.48px"
},
                            fontWeight: 400,
                            paddingBottom: {
 xs: "8px",
sm: "10px",
md: "12px"
}
                          }}
                        />
                        <DtvText
                          content={"Available for an additional cost."}
                          variant="body1"
                          sx={{
                            fontSize: {
 xs: "16px",
sm: "14px",
md: "20px"
},
                            color: colorPalette.neutral_white.main,
                            lineHeight: {
 xs: "20.8px",
sm: "18.2px",
md: "26px"
},
                            fontWeight: {
 xs: 400,
sm: 300,
md: 400
}
                          }}
                        />
                      </DtvBox>
                      {Object.keys(addOnChannels).length > 0 &&
                       Object.keys(addOnChannels).map((ctgKey: any) => {
                        if (ctgKey !== "" && addOnChannels[ctgKey]?.length > 0) {
                          const ctgValue = ctgKey;
                          return (
                            <AddOnIconCategory
                              channels={addOnChannels[ctgKey]}
                              ctgValue={ctgValue}
                              key={`banner-addOnCtg-${ctgValue}`}
                            />
                          );
                        }
                        return null;
                      })}
                  </DtvBox>
                );
              }
                return (<></>)
            }
            return null;
          })
          }
        </DtvBox>
      </DtvBox>
    );
  } catch (error) {
    console.error("Icon Banner Error >>", error);
    try {
      return <IconBannerErrorPage
        chnlSrc="streamclup"
        internetChnlsPDFLink={props?.internetChnlsPDFLink || null}
      />
    } catch (error) {
      console.error("Icon Banner FATAL Error >>", error);
      return <></>;
    }
  }
};

export const MemoizedIconBanner = React.memo(IconBannerWAddOnChnls);
